import type { FunctionPlugin } from "vue"
import * as Sentry from "@sentry/vue"
import { VueQueryPlugin } from "@tanstack/vue-query"
import { createHead } from "@unhead/vue/client"
import { detectIncognito } from "detectincognitojs"
import { createPinia } from "pinia"
import { registerSW } from "virtual:pwa-register"
import { createApp } from "vue"
import { configure } from "vue-gtag"
// @ts-expect-error borked lib types
import masonry from "vue-next-masonry"
import { initPwaInstall } from "@/composables/usePwaInstall"
import { i18n } from "@/i18n"
import router from "@/router"
import { vueQueryPluginOptions } from "@/vueQueryPluginOptions"
import App from "./App.vue"

import "./assets/fonts.css"
import "./assets/styles.css"

if (import.meta.env.VITE_BRAND) {
  import("@brand-css")
}

async function registerPWAConditionally() {
  try {
    const { isPrivate } = await detectIncognito()

    if (!isPrivate)
      // Use immediate: false to defer SW registration and reduce race conditions
      // with Cloudflare script injection, especially on Android WebView environments
      registerSW({ immediate: false })
  }
  catch {
    Sentry.withScope((scope) => {
      scope.setLevel("warning")

      Sentry.captureException(new Error("Failed to register Service Worker"))
    })
  }
}

async function init() {
  const app = createApp(App)
  const pinia = createPinia()
  const head = createHead({
    init: [{ title: `Welcome`, titleTemplate: `%s | ${import.meta.env.VITE_APP_NAME || "GUESTPIX"}` }],
  })

  app.use(head)
  app.use(masonry as FunctionPlugin<[]>)
  app.use(pinia)
  app.use(i18n)
  app.use(router)
  app.use(VueQueryPlugin, vueQueryPluginOptions)

  configure({
    tagId: import.meta.env.VITE_GTM_ID,
    pageTracker: {
      router,
    },
  })

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "production",
    sendDefaultPii: true,
    sampleRate: 0.75,
    integrations: [],
    ignoreErrors: [
      // Chunk-load failures — router.onError already handles recovery (page reload).
      // On iOS Safari, the global onunhandledrejection fires before router.onError,
      // so we suppress these here to avoid duplicate/spurious Sentry noise.
      /Importing a module script failed/i,
      /Failed to fetch dynamically imported module/i,
      /ChunkLoadError/i,
      /Loading chunk/i,
      /CSS_CHUNK_LOAD_FAILED/i,
      /Unable to preload CSS/i,
      /'text\/html' is not a valid JavaScript MIME type/i,
      /Network Error/i,
      /__firefox__/i,
      /DarkReader/i,
      /refresh_youtube_quality/i,
      /Can't find variable: __firefox__/i,
      /undefined is not an object \(evaluating 'window\.__firefox__/i,
      /undefined is not an object \(evaluating '(this\.globals\.)?window\.webkit\.messageHandlers'\)/i,
      /chrome-extension:/i,
      /moz-extension:/i,
      /safari-extension:/i,
      /webkit-masked-url:/i,
      /XHRInterface is not defined/i,
      /Cannot assign to read only property 'setRequestHeader'/i,
      /Cannot read properties of null \(reading '(document|contentWindow|contentDocument)'\)/i,
      /xbrowser is not defined/i,
      /swbrowser is not defined/i,
      /c\.isActive is not a function/i,
      /downProgCallback is not defined/i,
      /syncDownloadState is not defined/i,
    ],
    beforeSend(event, _hint) {
      if (event.user) {
        delete event.user.email
      }

      return event
    },
  })

  initPwaInstall()

  await router.isReady()

  app.mount("#app")

  await registerPWAConditionally()
}

init()
